/**
 * TrustGrid Evidence - Simple Card View
 * Shows controls and their linked evidence in a simple card layout
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spin, Tag, Empty, Row, Col, Input, Select, Button, Space } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  SearchOutlined,
  DownloadOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { listControls, getControlResults, getLatestScan, getScanSignals } from '../api/trustgridApi';
import { DataCard, LoadingState, NoData } from '../../../components/common';
import { showError } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';
import { colors, spacing } from '../../../theme/designTokens';

const { Search } = Input;
const { Option } = Select;

function EvidenceSimple() {
  const navigate = useNavigate();
  const { selectedProjectId, selectedProject, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;

  const [controls, setControls] = useState([]);
  const [controlResults, setControlResults] = useState({});
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [controlsRes, resultsRes, scanRes] = await Promise.all([
        listControls(),
        getControlResults(projectId),
        getLatestScan(projectId).catch(() => ({ data: null })),
      ]);

      setControls(controlsRes.data.controls || []);

      // Build map of control_code -> results
      const resultsMap = {};
      (resultsRes.data.results || []).forEach((result) => {
        resultsMap[result.control_code] = result;
      });
      setControlResults(resultsMap);

      // Get signals if scan exists
      if (scanRes.data && scanRes.data.id) {
        const signalsRes = await getScanSignals(scanRes.data.id);
        setSignals(signalsRes.data.signals || []);
      }
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'EvidenceSimple');
      if (!wasHandled) {
        console.error('Failed to fetch evidence data:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'pass') {
      return <CheckCircleOutlined style={{ color: colors.status.pass, fontSize: '20px' }} />;
    }
    return <CloseCircleOutlined style={{ color: colors.status.fail, fontSize: '20px' }} />;
  };

  const getStatusTag = (status) => {
    if (status === 'pass') {
      return <Tag color={colors.status.pass}>PASS</Tag>;
    }
    return <Tag color={colors.status.fail}>FAIL</Tag>;
  };

  // Filter controls based on search and filters
  const filteredControls = controls.filter((control) => {
    const signal = signals.find((s) => s.control_code === control.code);
    const status = signal?.status || 'unknown';

    // Search filter
    const matchesSearch =
      searchTerm === '' ||
      control.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      control.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (signal?.rationale || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    // Severity filter
    const matchesSeverity = severityFilter === 'all' || control.severity === severityFilter;

    return matchesSearch && matchesStatus && matchesSeverity;
  });

  // Calculate summary stats
  const passingCount = filteredControls.filter((c) => {
    const sig = signals.find((s) => s.control_code === c.code);
    return sig?.status === 'pass';
  }).length;

  const failingCount = filteredControls.filter((c) => {
    const sig = signals.find((s) => s.control_code === c.code);
    return sig?.status === 'fail';
  }).length;

  const totalEvidence = filteredControls.reduce((sum, c) => {
    const sig = signals.find((s) => s.control_code === c.code);
    return sum + (sig?.evidence_count || 0);
  }, 0);

  // Export to CSV
  const handleExportCSV = () => {
    const csvRows = [
      ['Control Code', 'Title', 'Status', 'Severity', 'Evidence Count', 'Rationale'].join(','),
    ];

    filteredControls.forEach((control) => {
      const signal = signals.find((s) => s.control_code === control.code);
      const status = signal?.status || 'unknown';
      const evidenceCount = signal?.evidence_count || 0;
      const rationale = (signal?.rationale || '').replace(/,/g, ';').replace(/\n/g, ' ');

      csvRows.push(
        [
          control.code,
          `"${control.title}"`,
          status,
          control.severity || 'N/A',
          evidenceCount,
          `"${rationale}"`,
        ].join(',')
      );
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evidence-simple-${selectedProject?.name || 'export'}-${
      new Date().toISOString().split('T')[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export to JSON
  const handleExportJSON = () => {
    const exportData = {
      project: selectedProject?.name || 'Unknown',
      exported_at: new Date().toISOString(),
      controls: filteredControls.map((control) => {
        const signal = signals.find((s) => s.control_code === control.code);
        return {
          code: control.code,
          title: control.title,
          description: control.description,
          severity: control.severity,
          status: signal?.status || 'unknown',
          evidence_count: signal?.evidence_count || 0,
          confidence: signal?.confidence || 0,
          rationale: signal?.rationale || '',
          evidence_refs: signal?.evidence_refs || [],
        };
      }),
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evidence-simple-${selectedProject?.name || 'export'}-${
      new Date().toISOString().split('T')[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <LoadingState type="spinner" />;
  }

  if (controls.length === 0) {
    return <NoData description="No controls found" />;
  }

  return (
    <div>
      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: spacing.lg }}>
        <Col span={8}>
          <DataCard
            title="Passing Controls"
            value={passingCount}
            valueColor={colors.status.pass}
            icon={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={8}>
          <DataCard
            title="Failing Controls"
            value={failingCount}
            valueColor={colors.status.fail}
            icon={<CloseCircleOutlined />}
          />
        </Col>
        <Col span={8}>
          <DataCard
            title="Total Evidence Items"
            value={totalEvidence}
            icon={<FileTextOutlined />}
          />
        </Col>
      </Row>

      {/* Filters & Export */}
      <Card style={{ marginBottom: spacing.md }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <Search
              placeholder="Search controls, titles, or rationale..."
              allowClear
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="pass">Pass Only</Option>
              <Option value="fail">Fail Only</Option>
            </Select>
          </Col>
          <Col xs={12} md={5}>
            <Select
              value={severityFilter}
              onChange={setSeverityFilter}
              style={{ width: '100%' }}
              placeholder="Filter by severity"
            >
              <Option value="all">All Severities</Option>
              <Option value="critical">Critical</Option>
              <Option value="high">High</Option>
              <Option value="medium">Medium</Option>
              <Option value="low">Low</Option>
            </Select>
          </Col>
          <Col xs={24} md={3}>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
                CSV
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportJSON}>
                JSON
              </Button>
            </Space>
          </Col>
        </Row>
        <div style={{ marginTop: spacing.sm, color: colors.text.secondary, fontSize: 14 }}>
          <FilterOutlined /> Showing {filteredControls.length} of {controls.length} controls
        </div>
      </Card>

      {/* Control Cards */}
      {filteredControls.length === 0 ? (
        <Card>
          <Empty description="No controls match your filters" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredControls.map((control) => {
            const result = controlResults[control.code];
            const signal = signals.find((s) => s.control_code === control.code);
            const status = signal?.status || 'unknown';
            const evidenceCount = signal?.evidence_count || 0;

            return (
              <Col xs={24} md={12} lg={8} key={control.code}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    borderLeft: `4px solid ${
                      status === 'pass' ? colors.status.pass : colors.status.fail
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: spacing.sm,
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0, color: colors.text.primary }}>{control.code}</h3>
                      {getStatusTag(status)}
                    </div>
                    {getStatusIcon(status)}
                  </div>

                  <p style={{ color: colors.text.secondary, fontSize: 14, minHeight: 60 }}>
                    {control.title}
                  </p>

                  <div
                    style={{
                      borderTop: `1px solid ${colors.border.light}`,
                      paddingTop: spacing.sm,
                      marginTop: spacing.sm,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                      <FileTextOutlined style={{ color: colors.primary[500] }} />
                      <span style={{ fontSize: 14, color: colors.text.secondary }}>
                        {evidenceCount} evidence item{evidenceCount !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {control.severity && (
                      <Tag
                        color={
                          control.severity === 'critical'
                            ? colors.severity.critical.main
                            : control.severity === 'high'
                            ? colors.severity.high.main
                            : control.severity === 'medium'
                            ? colors.severity.medium.main
                            : colors.severity.low.main
                        }
                      >
                        {control.severity.toUpperCase()}
                      </Tag>
                    )}
                  </div>

                  {signal?.rationale && (
                    <div
                      style={{
                        marginTop: spacing.sm,
                        padding: spacing.xs,
                        backgroundColor: colors.background.secondary,
                        borderRadius: 4,
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 12, color: colors.text.secondary }}>
                        {signal.rationale.substring(0, 120)}
                        {signal.rationale.length > 120 ? '...' : ''}
                      </p>
                    </div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}

export default EvidenceSimple;
